import * as Sentry from '@sentry/nextjs';
import { GetStaticPathsContext, GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { PHASE_DEVELOPMENT_SERVER, PHASE_PRODUCTION_BUILD } from 'next/constants';

import { CHANNEL, vacanciesSlugs } from '@boss/constants/corporate-jobs';
import { getEntriesByTag } from '@boss/contentful-client/corporate-jobs';
import { job } from '@boss/services';
import { Environment, SectionFields } from '@boss/types/corporate-jobs';
import { slugify } from '@boss/utils';

import { SEO } from '../../components';
import { PageTypes } from '../../constants/page';
import { JobsDetailPage } from '../../containers';
import Layout from '../../layout/default';
import { getServerSideTranslations } from '../../utils';
import { mapNextToContentfulLocaleMarket } from '../../utils/localeMapper';
import { getMappedNavigation } from '../../utils/navigation';
import { showJobPages } from '../../utils/options';

const IS_BUILD =
  (process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD && !process.env.SKIP_PRODUCT_BUILD) ||
  (process.env.NEXT_PHASE === PHASE_DEVELOPMENT_SERVER && process.env.DEBUG_BUILD === 'true');

const IS_DEV = process.env.NEXT_PHASE === PHASE_DEVELOPMENT_SERVER;

export const getStaticPaths = async ({ locales }: GetStaticPathsContext) => {
  try {
    const paths = [];

    if (!showJobPages) {
      return {
        paths: [],
        fallback: false,
      };
    }

    if (IS_BUILD && CHANNEL && locales) {
      for (const locale of locales.filter((locale: string) => locale !== 'default')) {
        const contentfulLocaleMarket = mapNextToContentfulLocaleMarket(locale);
        // Cast is to play along with the next-i18next config that types locales as a string

        for (const vacancy of await job.getVacancies({ locale: contentfulLocaleMarket })) {
          paths.push({
            locale,
            params: {
              slug: [slugify(vacancy.functieTitel), vacancy.vacatureId.toString()],
            },
          });
        }
      }
    }

    return {
      paths,
      fallback: IS_DEV ? 'blocking' : false,
    };
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        type: 'Fetching vacancies paths',
      },
    });

    if (IS_BUILD && error instanceof job.HroApiError) {
      throw error;
    }

    return { paths: [], fallback: IS_DEV ? 'blocking' : false };
  }
};

export const getStaticProps = async ({
  preview,
  params,
  locale,
}: GetStaticPropsContext<{ id: string; slug: string[] }>) => {
  const slug = params?.slug;
  const id = params?.slug[1];

  if (!slug || !locale || locale === 'default' || !CHANNEL || !id) {
    return {
      notFound: true,
    };
  }

  const contentfulLocaleMarket = mapNextToContentfulLocaleMarket(locale);

  try {
    const [navigation, vacancy, enrichedSections, aboutSections, spontaneousApplicationLink] = await Promise.all([
      getMappedNavigation(contentfulLocaleMarket, !!preview),
      job.getVacancy(id, contentfulLocaleMarket),
      getEntriesByTag<SectionFields>('displayLinkOtherJobsSiteDetail', contentfulLocaleMarket),
      getEntriesByTag<SectionFields>('displayAboutCompanySectionDetail', contentfulLocaleMarket),
      job.getSpontaneousApplicationLink(contentfulLocaleMarket, CHANNEL),
    ]);

    if (!vacancy) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        pageType: PageTypes.JOB_DETAIL,
        preview: !!preview,
        navigation,
        slug,
        locale,
        vacancy,
        enrichedSections,
        aboutSections,
        spontaneousApplicationLink,
        environment: process.env.CONTENTFUL_ENVIRONMENT as Environment,
        ...(await getServerSideTranslations(locale, ['common', 'jobs'])),
      },
    };
  } catch (error) {
    console.error(`[...slug] getStaticProps failed for slug "${slug?.join('/')}":`, error);

    if (IS_BUILD && error instanceof job.HroApiError) {
      throw error;
    }

    return { notFound: true };
  }
};

export const Vacancy = ({
  aboutSections,
  enrichedSections,
  environment,
  locale,
  navigation,
  preview,
  slug,
  vacancy,
  spontaneousApplicationLink,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const seoSlug = slug.join('/');

  return (
    <SEO
      description={vacancy.introTekst}
      imageSrc={vacancy.banner?.url}
      localizedSlugs={vacanciesSlugs}
      navigation={navigation}
      noFollow
      noIndex
      slug={seoSlug}
      title={vacancy.functieTitel}
    >
      <Layout environment={environment} localizedSlugs={vacanciesSlugs} navigation={navigation} preview={preview}>
        <JobsDetailPage
          aboutSections={aboutSections}
          enrichedSections={enrichedSections}
          locale={locale}
          spontaneousApplicationLink={spontaneousApplicationLink}
          vacancy={vacancy}
        />
      </Layout>
    </SEO>
  );
};

export default Vacancy;
